import React, { useContext, useState, useEffect } from 'react';
import { BasicModalWithFooter, Dropdown, RowHeader, StandAloneLink } from '@hz-design-system/web-ui';
import { Trans, useI18nContext } from '@ecg-marktplaats/js-react-i18n';

import experiments from '@/utils/labs/experiments';
import getSuitCSSClasses from '@/utils/getSuitCSSClasses';
import { linkGeneratorHelper, generateLinkObject } from '@/utils/links/linkGeneratorHelper';

import EnvironmentContext from '@/client/contexts/EnvironmentContext';
import getClientPageQueryObject from '@/client/utils/getClientPageQueryObject';
import pushToRouter from '@/client/utils/pushToRouter';

import CATEGORIES_L1 from '@/constants/categories';

type TAttributeRangeFacetDropdownProps = {
  searchRequestObject: TSearchRequestObject;
  attributeKey: string;
  options: {
    from?: [];
    to?: [];
  };
  range?: {
    from?: number;
    to?: number;
  };
  label: string;
  labelKey: string;
  preventScroll: boolean;
};

const AttributeRangeFacetDropdown = ({
  searchRequestObject,
  attributeKey = '',
  options,
  range = {},
  label = '',
  labelKey,
  preventScroll,
}: TAttributeRangeFacetDropdownProps) => {
  const { t, tExists } = useI18nContext();
  const {
    labsConfig,
    tenantContext: { tenant },
  } = useContext(EnvironmentContext);
  const { isFetchResultsOnceEnabled, isFetchResultsOnceAllCatEnabled, isFetchResultsHIOnceEnabled } = experiments({
    labsConfig,
  });
  const withAllAttributes =
    isFetchResultsOnceAllCatEnabled ||
    (isFetchResultsOnceEnabled && searchRequestObject.categories?.l1Category?.id === CATEGORIES_L1.DAMES_KLEREN) ||
    (isFetchResultsHIOnceEnabled &&
      tenant === 'mp' &&
      searchRequestObject.categories?.l1Category?.id === CATEGORIES_L1.HUIS_EN_INRICHTING);
  const [rangeState, setRangeState] = useState({
    attributeKey,
    from: range.from,
    to: range.to,
  });
  const [isRangeFacetModalOpen, setIsRangeFacetModalOpen] = useState(false);
  const titleLabelKey = labelKey || attributeKey;
  const helpTextKey = `facets.help.${attributeKey}`;
  const withAttributeHelpText = tExists(helpTextKey);

  useEffect(() => {
    if (Object.keys(range).length === 0 && !rangeState.from && !rangeState.to) {
      return;
    }

    setRangeState({
      attributeKey,
      from: range.from,
      to: range.to,
    });
  }, [attributeKey, range, rangeState.from, rangeState.to]);

  const rangeFilterHandler = ({ attributeKey: attrKey, from, to }) => {
    const otherRanges = searchRequestObject.attributeRanges.filter((attribute) => attribute.attributeKey !== attrKey);
    const linkOptions = {
      searchRequestObject,
      withAllAttributes,
      rangeAttributes: [
        ...otherRanges,
        {
          attributeKey: attrKey,
          ...(from && { from }),
          ...(to && { to }),
        },
      ],
    };

    if (preventScroll) {
      window.preventScroll = true;
    }

    const href = linkGeneratorHelper(linkOptions);
    const pageQuery = getClientPageQueryObject(generateLinkObject(linkOptions));
    pushToRouter({ query: pageQuery, href });
  };

  const handleOnChange = ({ target: { value } }: React.ChangeEvent<HTMLSelectElement>, subfield: string) => {
    const updatedState = { ...rangeState };
    updatedState[subfield] = Number(value);
    setRangeState(updatedState);
    rangeFilterHandler(updatedState);
  };

  return (
    <div className={getSuitCSSClasses({ namespace: 'hz', name: 'Filter' })}>
      <div className={getSuitCSSClasses({ namespace: 'hz', name: 'Filter-headingSection' })}>
        <RowHeader title={t(`facets.${titleLabelKey}`, label)} />
        {withAttributeHelpText && (
          <StandAloneLink className="ModalTrigger" onClick={() => setIsRangeFacetModalOpen(!isRangeFacetModalOpen)}>
            {t('readMore.linkText')}
          </StandAloneLink>
        )}
      </div>
      <div className={getSuitCSSClasses({ namespace: 'hz', name: 'Filter-fields', modifiers: ['dropdown'] })}>
        {Boolean(options.from) && (
          <Dropdown
            id={attributeKey}
            className={getSuitCSSClasses({ namespace: 'hz', name: 'Filter-dropdown' })}
            options={options.from}
            placeHolderText={t('facets.rangeFrom')}
            onChange={(e) => handleOnChange(e, 'from')}
            value={rangeState['from'] ? rangeState['from'].toString() : ''}
            ariaLabel={`${t(`facets.${titleLabelKey}`, label)} - ${t('facets.rangeFrom')}`}
          />
        )}
        {Boolean(options.to) && (
          <Dropdown
            id={attributeKey}
            className={getSuitCSSClasses({ namespace: 'hz', name: 'Filter-dropdown' })}
            options={options.to}
            placeHolderText={t('facets.rangeTo')}
            onChange={(e) => handleOnChange(e, 'to')}
            value={rangeState['to'] ? rangeState['to'].toString() : ''}
            ariaLabel={`${t(`facets.${titleLabelKey}`, label)} - ${t('facets.rangeTo')}`}
          />
        )}
        {isRangeFacetModalOpen && (
          <BasicModalWithFooter
            size="m"
            title={t(`facets.helpModalTitle.${attributeKey}`)}
            className="RangeFacetModal"
            onClose={() => setIsRangeFacetModalOpen(false)}
            buttons={[
              {
                component: 'PrimaryButton',
                text: t('facets.helpModalClose'),
                onClick: () => setIsRangeFacetModalOpen(false),
              },
            ]}
          >
            <Trans
              i18nKey={helpTextKey}
              paragraph-open={Trans.safeString('<p>')}
              paragraph-close={Trans.safeString('</p>')}
              bold-open={Trans.safeString('<div class="hz-Text hz-Text--bodyLargeStrong">')}
              bold-close={Trans.safeString('</div>')}
              break={Trans.safeString('<br>')}
            />
          </BasicModalWithFooter>
        )}
      </div>
    </div>
  );
};

export default AttributeRangeFacetDropdown;
